import { useEffect, useState } from "react";
import { SEO } from "@/components/SEO";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { PageHero } from "@/components/shared/PageHero";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  ArrowRight,
  CheckCircle2,
  ClipboardCheck,
  DollarSign,
  Link2,
  BarChart3,
  Headphones,
  ShieldCheck,
  BookOpen,
  User,
  Building2,
  ChevronDown,
  ChevronUp,
  XCircle,
  Handshake,
  FileText,
  CreditCard,
} from "lucide-react";
import { useState as useLocalState } from "react";
import { RebateProgramSection } from "@/components/affiliate/RebateProgramSection";

const steps = [
  {
    icon: ClipboardCheck,
    en: { title: "Apply", desc: "Submit your application in minutes. We review every application within 48 hours." },
    ar: { title: "تقدّم بطلبك", desc: "قدّم طلبك في دقائق. نراجع كل طلب خلال 48 ساعة." },
  },
  {
    icon: Link2,
    en: { title: "Get Approved", desc: "Receive access to your Affiliate Dashboard with referral tools and campaign creation." },
    ar: { title: "احصل على الموافقة", desc: "احصل على وصول للوحة تحكم الشريك مع أدوات الإحالة وإنشاء الحملات." },
  },
  {
    icon: Handshake,
    en: { title: "Facilitate Bookings", desc: "Find clients, present campaign options, and book on their behalf through the platform." },
    ar: { title: "سهّل الحجوزات", desc: "ابحث عن عملاء، اعرض خيارات الحملات، واحجز نيابة عنهم من خلال المنصة." },
  },
  {
    icon: DollarSign,
    en: { title: "Earn Commissions", desc: "Earn a percentage on every confirmed booking. Track earnings in real time." },
    ar: { title: "اكسب العمولات", desc: "اكسب نسبة على كل حجز مؤكد. تتبّع أرباحك لحظةً بلحظة." },
  },
];

const benefits = [
  {
    icon: DollarSign,
    en: { title: "Competitive Commissions", desc: "Earn a generous percentage on every confirmed campaign booking you facilitate." },
    ar: { title: "عمولات تنافسية", desc: "احصل على نسبة مجزية على كل حجز حملة مؤكد تسهّله." },
  },
  {
    icon: BarChart3,
    en: { title: "Full Affiliate Dashboard", desc: "Create campaigns, track bookings, manage clients, and monitor commissions — all in one place." },
    ar: { title: "لوحة تحكم كاملة", desc: "أنشئ حملات، تتبّع الحجوزات، أدر العملاء، وراقب العمولات — في مكان واحد." },
  },
  {
    icon: FileText,
    en: { title: "Platform-Generated Documents", desc: "Access invoices, contracts, and proposals — all generated and controlled by the platform." },
    ar: { title: "مستندات من المنصة", desc: "وصول للفواتير والعقود والمقترحات — جميعها تُنتج وتُدار من المنصة." },
  },
  {
    icon: Headphones,
    en: { title: "Dedicated Support", desc: "A dedicated account manager to help you succeed and answer your questions." },
    ar: { title: "دعم متخصص", desc: "مدير حساب متخصص لمساعدتك على النجاح والإجابة على استفساراتك." },
  },
  {
    icon: ShieldCheck,
    en: { title: "No Upfront Cost", desc: "Joining is completely free. No investment required — just bring your network." },
    ar: { title: "بدون تكلفة مسبقة", desc: "الانضمام مجاني تماماً. لا يلزم أي استثمار — فقط أحضر شبكتك." },
  },
  {
    icon: CreditCard,
    en: { title: "Platform Handles Everything", desc: "We handle pricing, contracts, payments, and campaign execution. You focus on clients." },
    ar: { title: "المنصة تتولى كل شيء", desc: "نتولى التسعير والعقود والمدفوعات وتنفيذ الحملات. أنت ركّز على العملاء." },
  },
];

const partnersDo = [
  { en: "Identify and engage potential advertisers", ar: "تحديد المعلنين المحتملين والتواصل معهم" },
  { en: "Present DOOH campaign options and pricing", ar: "عرض خيارات حملات DOOH والتسعير" },
  { en: "Manage the client relationship end-to-end", ar: "إدارة علاقة العميل بالكامل" },
  { en: "Share platform-generated proposals and invoices", ar: "مشاركة المقترحات والفواتير المُولّدة من المنصة" },
  { en: "Complete bookings on behalf of clients", ar: "إتمام الحجوزات نيابة عن العملاء" },
  { en: "Facilitate agreement and payment collection", ar: "تسهيل الاتفاق وتحصيل المدفوعات" },
];

const partnersDont = [
  { en: "Modify pricing or contractual terms", ar: "تعديل الأسعار أو الشروط التعاقدية" },
  { en: "Create custom contracts outside the platform", ar: "إنشاء عقود مخصصة خارج المنصة" },
  { en: "Independently execute or deliver campaigns", ar: "تنفيذ أو تسليم الحملات بشكل مستقل" },
  { en: "Control inventory or campaign scheduling", ar: "التحكم في المخزون أو جدولة الحملات" },
];

const platformHandles = [
  { en: "Pricing and inventory management", ar: "إدارة التسعير والمخزون" },
  { en: "Generating all documents (contracts, invoices, proposals)", ar: "إنتاج جميع المستندات (العقود، الفواتير، المقترحات)" },
  { en: "Processing transactions and payments", ar: "معالجة المعاملات والمدفوعات" },
  { en: "Finalizing and confirming all bookings", ar: "إنهاء وتأكيد جميع الحجوزات" },
  { en: "Executing and delivering campaigns", ar: "تنفيذ وتسليم الحملات" },
  { en: "Providing operational and technical support", ar: "توفير الدعم التشغيلي والتقني" },
];

const faqs = [
  {
    en: { q: "What is a Referral Partner?", a: "A Referral Partner is someone who identifies potential advertisers, facilitates campaign bookings, and manages the client relationship. The platform handles all backend processing, pricing, and execution." },
    ar: { q: "ما هو شريك الإحالة؟", a: "شريك الإحالة هو شخص يحدد المعلنين المحتملين، ويسهّل حجوزات الحملات، ويدير علاقة العميل. المنصة تتولى جميع عمليات المعالجة والتسعير والتنفيذ." },
  },
  {
    en: { q: "Can I book campaigns on behalf of my clients?", a: "Yes! As an approved partner, you can create and submit campaigns directly through your Affiliate Dashboard on behalf of your clients." },
    ar: { q: "هل يمكنني حجز حملات نيابة عن عملائي؟", a: "نعم! بصفتك شريكاً معتمداً، يمكنك إنشاء وتقديم الحملات مباشرة من خلال لوحة تحكم الشريك نيابة عن عملائك." },
  },
  {
    en: { q: "How much do I earn?", a: "Commissions are competitive and calculated as a percentage of confirmed campaign bookings. Contact us for the current rate." },
    ar: { q: "كم أكسب؟", a: "العمولات تنافسية وتُحسب كنسبة من حجوزات الحملات المؤكدة. تواصل معنا للاطلاع على النسبة الحالية." },
  },
  {
    en: { q: "Does the client need to interact with the platform?", a: "No. You can handle the entire client relationship. The client can receive proposals, agree to campaigns, and complete payment through you." },
    ar: { q: "هل يحتاج العميل للتعامل مع المنصة؟", a: "لا. يمكنك التعامل مع علاقة العميل بالكامل. يمكن للعميل استلام المقترحات والموافقة على الحملات وإتمام الدفع من خلالك." },
  },
  {
    en: { q: "When do I get paid?", a: "Commissions are paid out after the campaign booking is confirmed and payment is received from the client." },
    ar: { q: "متى يتم الدفع لي؟", a: "تُدفع العمولات بعد تأكيد حجز الحملة واستلام الدفع من العميل." },
  },
];

const FAQItem = ({ faq, isAr }: { faq: typeof faqs[0]; isAr: boolean }) => {
  const [open, setOpen] = useLocalState(false);
  const content = isAr ? faq.ar : faq.en;
  return (
    <div className="border border-border/50 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-muted/40 transition-colors"
      >
        <span className="font-semibold text-foreground">{content.q}</span>
        {open ? <ChevronUp className="h-4 w-4 text-muted-foreground shrink-0" /> : <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />}
      </button>
      {open && <div className="px-6 pb-4 text-muted-foreground text-sm leading-relaxed">{content.a}</div>}
    </div>
  );
};

export default function AffiliateProgram() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isAr = language === "ar";
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(t);
  }, []);

  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="relative min-h-screen">
      <SEO
        title="Referral Partner Program — Fusion Media Affiliates"
        description="Join the Fusion Media Referral Partner Program. Earn commissions by bringing advertisers to book DOOH and influencer campaigns."
      />

      <div className="relative max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <PageHero
          eyebrow={isAr ? "برنامج شركاء الإحالة" : "Referral Partner Program"}
          title={isAr ? "اكسب من تسهيل" : "Earn by facilitating"}
          accent={isAr ? "حجوزات DOOH" : "DOOH bookings"}
          subtitle={
            isAr
              ? "انضم لبرنامج شركاء الإحالة واكسب عمولات عبر جلب المعلنين لحجز الحملات. أنت تدير علاقة العميل — نحن نتولى الباقي."
              : "Join our Referral Partner Program and earn commissions by bringing advertisers to book campaigns. You manage the client relationship — we handle the rest."
          }
          centered
          ctas={[
            { label: isAr ? "قدّم طلبك الآن" : "Apply Now", to: "/affiliate-signup" },
            {
              label: isAr ? "اعرف أكثر" : "Learn More",
              variant: "secondary",
              onClick: () => scrollToSection("how-it-works"),
            },
          ]}
        />

        <section className="pb-12 md:pb-16">
          <div className="grid grid-cols-3 gap-4 md:gap-6 max-w-2xl mx-auto">
            {[
              { value: "48h", en: "Review time", ar: "وقت المراجعة" },
              { value: "100%", en: "Free to join", ar: "مجاني للانضمام" },
              { value: "∞", en: "Earning potential", ar: "إمكانات الكسب" },
            ].map((stat) => (
              <div key={stat.value} className="bg-card border border-border/60 rounded-2xl p-4 text-center shadow-card">
                <div className="text-2xl md:text-3xl font-bold text-primary tabular-nums">{stat.value}</div>
                <div className="text-xs text-muted-foreground mt-1">{isAr ? stat.ar : stat.en}</div>
              </div>
            ))}
          </div>
        </section>

      {/* ── HOW IT WORKS ── */}
      <section id="how-it-works" className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-3 text-foreground">{isAr ? "كيف يعمل البرنامج" : "How It Works"}</h2>
          <p className="text-center text-muted-foreground mb-12">{isAr ? "أربع خطوات بسيطة للبدء في الكسب" : "Four simple steps to start earning"}</p>
          <div className="grid md:grid-cols-4 gap-6">
            {steps.map((step, i) => {
              const Icon = step.icon;
              const content = isAr ? step.ar : step.en;
              return (
                <div key={i} className="relative flex flex-col items-center text-center group">
                  {i < steps.length - 1 && <div className="hidden md:block absolute top-10 left-[calc(50%+2rem)] w-[calc(100%-4rem)] h-px border-t-2 border-dashed border-border/60" />}
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-4 group-hover:scale-105 transition-transform duration-300 shadow-sm">
                    <Icon className="h-7 w-7 text-primary" />
                  </div>
                  <div className="text-xs font-bold text-primary/70 mb-1">{isAr ? `الخطوة ${i + 1}` : `Step ${i + 1}`}</div>
                  <h3 className="text-base font-bold text-foreground mb-2">{content.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{content.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── ADMIN-ONLY: VALUE REBATE SYSTEM PREVIEW ── */}
      <RebateProgramSection />

      {/* ── BENEFITS ── */}
      <section className="py-20 px-4 bg-muted/30">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-3 text-foreground">{isAr ? "لماذا تنضم؟" : "Why Join?"}</h2>
          <p className="text-center text-muted-foreground mb-12">{isAr ? "كل ما تحتاجه لتنجح كشريك إحالة" : "Everything you need to succeed as a referral partner"}</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((b, i) => {
              const Icon = b.icon;
              const content = isAr ? b.ar : b.en;
              return (
                <div key={i} className="bg-card border border-border/50 rounded-2xl p-6 hover:shadow-md transition-shadow duration-300">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/15 to-secondary/15 flex items-center justify-center mb-4">
                    <Icon className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">{content.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{content.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── WHAT PARTNERS DO / DON'T DO ── */}
      <section className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 text-foreground">{isAr ? "دور شريك الإحالة" : "Role of a Referral Partner"}</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {/* Partners Do */}
            <div className="bg-gradient-to-br from-green-500/5 to-green-500/10 border border-green-500/20 rounded-2xl p-6">
              <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                {isAr ? "ما يفعله الشركاء" : "What Partners Do"}
              </h3>
              <ul className="space-y-3">
                {partnersDo.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
                    {isAr ? item.ar : item.en}
                  </li>
                ))}
              </ul>
            </div>

            {/* Partners Don't Do */}
            <div className="bg-gradient-to-br from-red-500/5 to-red-500/10 border border-red-500/20 rounded-2xl p-6">
              <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                <XCircle className="h-5 w-5 text-red-500" />
                {isAr ? "ما لا يفعله الشركاء" : "What Partners Don't Do"}
              </h3>
              <ul className="space-y-3">
                {partnersDont.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <XCircle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
                    {isAr ? item.ar : item.en}
                  </li>
                ))}
              </ul>
            </div>

            {/* Platform Handles */}
            <div className="bg-gradient-to-br from-primary/5 to-secondary/10 border border-primary/20 rounded-2xl p-6">
              <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-primary" />
                {isAr ? "ما تتولاه المنصة" : "What the Platform Handles"}
              </h3>
              <ul className="space-y-3">
                {platformHandles.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    {isAr ? item.ar : item.en}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── WHO CAN JOIN ── */}
      <section className="py-20 px-4 bg-muted/30">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-3 text-foreground">{isAr ? "من يمكنه الانضمام؟" : "Who Can Join?"}</h2>
          <p className="text-center text-muted-foreground mb-12">{isAr ? "البرنامج مفتوح للأفراد والوكالات" : "The program is open to individuals and agencies"}</p>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-primary/5 to-secondary/5 border border-primary/20 rounded-2xl p-8 hover:border-primary/40 transition-colors duration-300">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-5"><User className="h-7 w-7 text-primary" /></div>
              <h3 className="text-xl font-bold text-foreground mb-2">{isAr ? "الأفراد" : "Individuals"}</h3>
              <p className="text-muted-foreground text-sm mb-5 leading-relaxed">{isAr ? "مستقلون، محترفو مبيعات، مسوّقون، وأي شخص لديه شبكة علاقات." : "Freelancers, marketing professionals, and anyone with a strong network of potential advertisers."}</p>
              <ul className="space-y-2">
                {(isAr ? ["مستقل أو عامل حر", "مسوّق رقمي", "مستشار أعمال", "صاحب علاقات واسعة"] : ["Freelancer", "Digital marketer", "Business consultant", "Well-connected professional"]).map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm text-muted-foreground"><CheckCircle2 className="h-4 w-4 text-primary shrink-0" />{item}</li>
                ))}
              </ul>
            </div>
            <div className="bg-gradient-to-br from-secondary/5 to-primary/5 border border-secondary/20 rounded-2xl p-8 hover:border-secondary/40 transition-colors duration-300">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-secondary/20 to-primary/20 flex items-center justify-center mb-5"><Building2 className="h-7 w-7 text-secondary" /></div>
              <h3 className="text-xl font-bold text-foreground mb-2">{isAr ? "الوكالات" : "Agencies"}</h3>
              <p className="text-muted-foreground text-sm mb-5 leading-relaxed">{isAr ? "وكالات التسويق، شركات شراء الوسائط، وأي وكالة تخدم عملاء الإعلانات." : "Marketing agencies, media buying firms, and any agency serving advertising clients."}</p>
              <ul className="space-y-2">
                {(isAr ? ["وكالة تسويق", "شركة شراء وسائط", "وكالة إبداعية", "شركة علاقات عامة"] : ["Marketing agency", "Media buying firm", "Creative agency", "PR company"]).map((item) => (
                  <li key={item} className="flex items-center gap-2 text-sm text-muted-foreground"><CheckCircle2 className="h-4 w-4 text-secondary shrink-0" />{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── BOOKING FLOW ── */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-3 text-foreground">{isAr ? "كيف تعمل عملية الحجز" : "How the Booking Flow Works"}</h2>
          <p className="text-center text-muted-foreground mb-12">{isAr ? "من العميل إلى الحملة — أنت الوسيط، ونحن المنفذ" : "From client to campaign — you facilitate, we execute"}</p>
          <div className="space-y-4">
            {[
              { en: "Partner identifies and engages a potential advertiser", ar: "الشريك يحدد ويتواصل مع معلن محتمل" },
              { en: "Partner presents campaign options and platform-generated documents", ar: "الشريك يعرض خيارات الحملة والمستندات المُولّدة من المنصة" },
              { en: "Client agrees to proceed through the partner", ar: "العميل يوافق على المتابعة من خلال الشريك" },
              { en: "Partner collects confirmations and submits the booking on the platform", ar: "الشريك يجمع التأكيدات ويقدّم الحجز على المنصة" },
              { en: "Payment is processed through the platform", ar: "يتم معالجة الدفع من خلال المنصة" },
              { en: "Platform generates final documentation and confirms the campaign", ar: "المنصة تُولّد الوثائق النهائية وتؤكد الحملة" },
              { en: "Campaign is executed by the platform", ar: "المنصة تنفذ الحملة" },
            ].map((step, i) => (
              <div key={i} className="flex items-start gap-4 bg-card border border-border/50 rounded-xl p-4">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <span className="text-sm font-bold text-primary">{i + 1}</span>
                </div>
                <p className="text-sm text-foreground pt-1">{isAr ? step.ar : step.en}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-20 px-4 bg-muted/30">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-3 text-foreground">{isAr ? "الأسئلة الشائعة" : "Frequently Asked Questions"}</h2>
          <p className="text-center text-muted-foreground mb-10">{isAr ? "إجابات على أكثر الأسئلة شيوعاً" : "Quick answers to common questions"}</p>
          <div className="space-y-3">
            {faqs.map((faq, i) => <FAQItem key={i} faq={faq} isAr={isAr} />)}
          </div>
        </div>
      </section>

      {/* ── BOTTOM CTA ── */}
      <section className="py-24 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-foreground">{isAr ? "مستعد للبدء في الكسب؟" : "Ready to Start Earning?"}</h2>
          <p className="text-muted-foreground mb-8 text-lg">{isAr ? "قدّم طلبك الآن وانضم لشبكة شركاء الإحالة الناجحين." : "Apply now and join a network of successful referral partners facilitating premium DOOH campaigns."}</p>
          <Button size="lg" className="bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 text-primary-foreground px-10 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5 group text-base" onClick={() => navigate("/affiliate-signup")}>
            {isAr ? "قدّم طلبك الآن" : "Apply Now — It's Free"}
            <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </section>
      </div>
    </div>
  );
}
